import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Copy,
  Check,
  ShieldCheck,
  Globe,
  Trash2,
  KeyRound,
  Layers,
  Plus,
  X
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "../components/CustomDropdown";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const FreshdeskConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [config, setConfig] = useState({
    api_key: "",
    freshdesk_domain: "",
    is_active: false,
    group_mappings: {},
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [freshdeskFields, setFreshdeskFields] = useState([]);
  const [selectedField, setSelectedField] = useState("");
  const [fieldMode, setFieldMode] = useState("llm");
  const [selectedValue, setSelectedValue] = useState("");
  const [addedCustomFields, setAddedCustomFields] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadTicketFields = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshdesk/ticket-fields", { aiAgentId: activeAiAgentId });
      if (response.success && response.fields) {
        const sorted = [...response.fields].sort((a, b) =>
          (a.label || "").localeCompare(b.label || "")
        );
        setFreshdeskFields(sorted);
      }
    } catch (e) {
      console.error("Failed to load ticket fields:", e);
    }
  }, [activeAiAgentId]);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshdesk/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setConfig({
          api_key: response.config.api_key ? UNCHANGED_KEY : "",
          freshdesk_domain: response.config.freshdesk_domain || "",
          is_active: response.config.is_active || false,
          group_mappings: response.config.group_mappings || {},
        });
        setWebhookUrl(response.config.webhook_url || "");
        setTokenId(response.config.token_id || "");
      }
    } catch (error) {
      console.error("Failed to load Freshdesk config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
    loadTicketFields();
  }, [loadConfig, loadTicketFields]);

  const saveConfig = async () => {
    if (!config.api_key.trim()) {
      showToast("Please enter your API key", "error");
      return;
    }

    if (!config.freshdesk_domain.trim()) {
      showToast("Please enter your Freshdesk domain", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/freshdesk/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...config,
          api_key: config.api_key === UNCHANGED_KEY ? undefined : config.api_key,
        }),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        setTokenId(response.token_id);
        showToast("Freshdesk configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/freshdesk", {
        method: "DELETE",
      });
      if (response.success) {
        setConfig({
          api_key: "",
          freshdesk_domain: "",
          is_active: false,
          group_mappings: {},
        });
        setWebhookUrl("");
        setTokenId("");
        showToast("Freshdesk integration deleted successfully!", "success");
      }
    } catch (error) {
      // Handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopiedWebhook(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopiedWebhook(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const copyTokenId = async () => {
    try {
      await navigator.clipboard.writeText(tokenId);
      setCopiedToken(true);
      showToast("Token ID copied to clipboard!", "success");
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (error) {
      showToast("Failed to copy token", "error");
    }
  };

  const toggleActive = async () => {
    if (!config.is_active) {
      if (!config.api_key?.trim() || !config.freshdesk_domain?.trim()) {
        showToast("Please enter and save your API key and domain first", "error");
        return;
      }
      if (config.api_key !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !config.is_active;
      const response = await ApiClient.request("/freshdesk/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          is_active: newActiveState,
          freshdesk_domain: config.freshdesk_domain,
          group_mappings: config.group_mappings,
        }),
      });

      if (response.success) {
        setConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(
          newActiveState
            ? "Freshdesk activated!"
            : "Freshdesk deactivated.",
          "success"
        );
      }
    } catch (error) {
      // Handled by ApiClient global toast
    }
  };

  const saveGroupMappings = async (updatedMappings) => {
    try {
      const response = await ApiClient.request("/freshdesk/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...config,
          group_mappings: updatedMappings,
        }),
      });
      if (response.success) {
        showToast("Group mappings saved successfully!", "success");
      }
    } catch (error) {
      showToast("Failed to save group mappings", "error");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2.5">
              <img src="/freshdesk.png" alt="Freshdesk" className="w-8 h-8 object-contain rounded-md flex-shrink-0" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Freshdesk Ticket Integration</h1>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Connect your AI agent to automatically triage and resolve Freshdesk tickets.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              config.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {config.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                config.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={config.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  config.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Credentials Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Account Credentials</h2>
              <p className="text-[11px] text-zinc-500">Provide your Freshdesk domain and personal API key</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Freshdesk Domain
              </label>
              <input
                type="text"
                value={config.freshdesk_domain}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    freshdesk_domain: e.target.value,
                  })
                }
                placeholder="yourcompany.freshdesk.com"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Enter your full helpdesk domain, e.g. <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">acme.freshdesk.com</code> (without http/https).
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                API Key
              </label>
              <input
                type="password"
                value={config.api_key === UNCHANGED_KEY ? MASK_DISPLAY : config.api_key}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    api_key: e.target.value,
                  })
                }
                onFocus={() => {
                  if (config.api_key === UNCHANGED_KEY) {
                    setConfig({ ...config, api_key: "" });
                  }
                }}
                placeholder={config.api_key === UNCHANGED_KEY ? MASK_DISPLAY : "Your Freshdesk API key"}
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Found in your <span className="text-zinc-600 font-medium">Profile Settings → API Key</span> in your Freshdesk portal.
              </p>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {config.freshdesk_domain ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Freshdesk</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !config.freshdesk_domain?.trim() || !config.api_key?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Configuration"}</span>
            </button>
          </div>
        </div>

        {/* Escalation Group Mappings Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <Layers className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Escalation Group Mappings</h2>
              <p className="text-[11px] text-zinc-500">Map internal support department keys to Freshdesk ticket group IDs</p>
            </div>
          </div>

          <div className="space-y-4">
            {Object.keys(config.group_mappings || {}).length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs text-zinc-500">No escalation group mappings configured yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(config.group_mappings).map(([dept, val]) => {
                  const isRich = val && typeof val === "object";
                  const groupId = isRich ? val.group_id : val;
                  const customFieldsStr = isRich && val.custom_fields ? JSON.stringify(val.custom_fields) : "";
                  return (
                    <div key={dept} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200 gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="font-mono text-xs font-semibold text-zinc-900">{dept}</span>
                        <span className="text-xs text-zinc-500">→ Group ID: {groupId}</span>
                        {customFieldsStr && (
                          <span className="text-[10px] text-zinc-600 bg-zinc-200/60 px-2 py-0.5 rounded font-mono truncate max-w-xs">
                            Fields: {customFieldsStr}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = { ...config.group_mappings };
                          delete updated[dept];
                          setConfig({ ...config, group_mappings: updated });
                          saveGroupMappings(updated);
                        }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium self-end sm:self-auto transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add New Group Mapping Form */}
            <div className="p-3.5 sm:p-4 bg-zinc-50/50 rounded-lg border border-zinc-200 space-y-3">
              <h4 className="text-xs font-semibold text-zinc-900">Add Group Mapping</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-zinc-600 mb-1">
                    Department Key (e.g. l1_support)
                  </label>
                  <input
                    type="text"
                    id="newDeptKey"
                    placeholder="l1_support"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-zinc-200 rounded-md focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-zinc-600 mb-1">
                    Freshdesk Group ID
                  </label>
                  <input
                    type="number"
                    id="newGroupId"
                    placeholder="1070000306462"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-zinc-200 rounded-md focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono"
                  />
                </div>
              </div>

              {/* Custom Ticket Fields staged */}
              {freshdeskFields.length > 0 && (
                <div className="pt-3 border-t border-zinc-200/80 space-y-2.5">
                  <div className="text-[11px] font-medium text-zinc-700">
                    Custom Ticket Field Rules (Optional)
                  </div>

                  {Object.keys(addedCustomFields).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(addedCustomFields).map(([cfKey, cfVal]) => {
                        const fieldLabel = freshdeskFields.find((f) => f.name === cfKey)?.label || cfKey;
                        return (
                          <div
                            key={cfKey}
                            className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-100 text-zinc-800 text-[11px] rounded-md border border-zinc-200"
                          >
                            <span>{fieldLabel}: {cfVal === "LLM_SELECT" ? "AI Selected" : cfVal}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const nextFields = { ...addedCustomFields };
                                delete nextFields[cfKey];
                                setAddedCustomFields(nextFields);
                              }}
                              className="text-zinc-400 hover:text-red-600 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 items-end">
                    <div>
                      <label className="block text-[10px] text-zinc-500 mb-1">Select Field</label>
                      <CustomDropdown
                        placeholder="Select field"
                        options={[
                          { value: "", label: "-- Select field --" },
                          ...freshdeskFields.map((f) => ({ value: f.name, label: `${f.label} (${f.name})` })),
                        ]}
                        value={selectedField}
                        onChange={(val) => {
                          setSelectedField(val);
                          setSelectedValue("");
                        }}
                      />
                    </div>

                    {selectedField && (
                      <>
                        <div>
                          <label className="block text-[10px] text-zinc-500 mb-1">Value Mode</label>
                          <CustomDropdown
                            placeholder="Select mode"
                            options={[
                              { value: "llm", label: "AI dynamically chooses" },
                              { value: "constant", label: "Fixed constant value" },
                            ]}
                            value={fieldMode}
                            onChange={(val) => {
                              setFieldMode(val);
                              setSelectedValue("");
                            }}
                          />
                        </div>

                        {fieldMode === "constant" && (() => {
                          const currentField = freshdeskFields.find((f) => f.name === selectedField);
                          const hasChoices = currentField && currentField.choices && currentField.choices.length > 0;
                          return (
                            <div>
                              <label className="block text-[10px] text-zinc-500 mb-1">Constant Value</label>
                              {hasChoices ? (
                                <CustomDropdown
                                  placeholder="Select option"
                                  options={[
                                    { value: "", label: "-- Select option --" },
                                    ...currentField.choices.map((c) => ({ value: c, label: c })),
                                  ]}
                                  value={selectedValue}
                                  onChange={(val) => setSelectedValue(val)}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={selectedValue}
                                  onChange={(e) => setSelectedValue(e.target.value)}
                                  placeholder="Enter value..."
                                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 focus:outline-none focus:border-zinc-900"
                                />
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  {selectedField && (
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (fieldMode === "constant" && !selectedValue) {
                            showToast("Please select a constant value", "error");
                            return;
                          }
                          const val = fieldMode === "llm" ? "LLM_SELECT" : selectedValue;
                          setAddedCustomFields((prev) => ({
                            ...prev,
                            [selectedField]: val,
                          }));
                          setSelectedField("");
                          setSelectedValue("");
                          setFieldMode("llm");
                        }}
                        className="px-3 py-1 text-xs font-medium text-zinc-800 bg-zinc-200 hover:bg-zinc-300 rounded-md transition-colors"
                      >
                        Add Field Rule
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    const keyEl = document.getElementById("newDeptKey");
                    const idEl = document.getElementById("newGroupId");
                    const key = keyEl?.value?.trim()?.toLowerCase()?.replace(/\s+/g, "_");
                    const id = parseInt(idEl?.value?.trim());

                    if (!key) {
                      showToast("Please enter a department key", "error");
                      return;
                    }
                    if (isNaN(id)) {
                      showToast("Please enter a valid Freshdesk Group ID", "error");
                      return;
                    }

                    const isIdDuplicate = Object.values(config.group_mappings || {}).some((val) => {
                      const existingId = typeof val === "object" ? val.group_id : val;
                      return existingId === id;
                    });
                    if (isIdDuplicate) {
                      showToast(`Group ID ${id} is already mapped to another department!`, "error");
                      return;
                    }

                    let customFields = null;
                    if (Object.keys(addedCustomFields).length > 0) {
                      customFields = addedCustomFields;
                    } else if (selectedField) {
                      const val = fieldMode === "llm" ? "LLM_SELECT" : selectedValue;
                      if (fieldMode === "constant" && !val) {
                        showToast("Please select a constant value for the custom field", "error");
                        return;
                      }
                      customFields = { [selectedField]: val };
                    }

                    const val = customFields ? { group_id: id, custom_fields: customFields } : id;

                    const updated = {
                      ...(config.group_mappings || {}),
                      [key]: val,
                    };
                    setConfig({ ...config, group_mappings: updated });
                    saveGroupMappings(updated);

                    if (keyEl) keyEl.value = "";
                    if (idEl) idEl.value = "";
                    setSelectedField("");
                    setSelectedValue("");
                    setFieldMode("llm");
                    setAddedCustomFields({});
                  }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 text-xs font-medium transition-colors"
                >
                  <Plus size={13} />
                  <span>Add Mapping</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook and Token Card */}
        {webhookUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-4">
            <div>
              <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
                <Globe className="text-zinc-800 shrink-0" size={18} />
                <div>
                  <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Live Webhook Endpoint</h3>
                  <p className="text-[11px] text-zinc-500">Destination endpoint for Freshdesk ticket trigger events</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200 mt-3">
                <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                  {displayUrl}
                </div>
                <button
                  type="button"
                  onClick={copyWebhookUrl}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                    copiedWebhook
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                  }`}
                  title={copiedWebhook ? "Copied!" : "Copy Webhook URL"}
                >
                  {copiedWebhook ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                  <span>{copiedWebhook ? "Copied" : "Copy URL"}</span>
                </button>
              </div>
            </div>

            <div className="pt-3 border-t border-zinc-100">
              <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
                <KeyRound className="text-zinc-800 shrink-0" size={18} />
                <div>
                  <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Webhook Token ID</h3>
                  <p className="text-[11px] text-zinc-500">Unique authentication token for incoming payloads</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200 mt-3">
                <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                  {tokenId || "—"}
                </div>
                <button
                  type="button"
                  onClick={copyTokenId}
                  disabled={!tokenId}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                    copiedToken
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100 disabled:opacity-40"
                  }`}
                  title={copiedToken ? "Copied!" : "Copy Token"}
                >
                  {copiedToken ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                  <span>{copiedToken ? "Copied" : "Copy Token"}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Freshdesk?"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default FreshdeskConfig;
